package xyz.malefic.gallery.pages

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateMapOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.css.CSSLengthOrPercentageNumericValue
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.FontWeight.Companion.Medium
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment.CenterHorizontally
import com.varabyte.kobweb.compose.ui.Alignment.CenterVertically
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.flexWrap
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.compose.ui.modifiers.minHeight
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.layout.Surface
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.theme.breakpoint.rememberBreakpoint
import kotlinx.browser.window
import kotlinx.serialization.json.Json
import org.jetbrains.compose.web.css.FlexWrap
import org.jetbrains.compose.web.css.keywords.auto
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.vh
import org.w3c.xhr.XMLHttpRequest
import xyz.malefic.gallery.ui.FolderView
import xyz.malefic.gallery.ui.GalleryIndex
import xyz.malefic.gallery.ui.ImageCard
import xyz.malefic.gallery.ui.ThemeColors.background
import xyz.malefic.gallery.ui.ThemeColors.surface
import xyz.malefic.gallery.ui.ThemeColors.text
import xyz.malefic.gallery.ui.ThemeColors.textSecondary
import xyz.malefic.gallery.ui.ThemeToggleButton
import xyz.malefic.gallery.ui.defaultBoxShadow

private const val URL = "https://gallery.malefic.xyz/photos"

@Page
@Composable
fun GalleryPage() {
    var galleryData by remember { mutableStateOf<GalleryIndex?>(null) }
    val breakpoint = rememberBreakpoint()

    LaunchedEffect(Unit) {
        fetchGalleryData { data -> galleryData = data }
    }

    Surface(
        modifier =
            Modifier
                .fillMaxWidth()
                .minHeight(100.vh)
                .backgroundColor(background),
    ) {
        Column(
            modifier =
                Modifier
                    .fillMaxWidth()
                    .maxWidth(1200.px)
                    .margin(leftRight = auto.unsafeCast<CSSLengthOrPercentageNumericValue>())
                    .padding(
                        top = 32.px,
                        bottom = 32.px,
                        left = if (breakpoint >= Breakpoint.MD) 64.px else 16.px,
                        right = if (breakpoint >= Breakpoint.MD) 64.px else 16.px,
                    ),
            horizontalAlignment = CenterHorizontally,
        ) {
            Header()
            galleryData?.let { data ->
                GalleryContent(data)
            } ?: LoadingIndicator()
        }
    }
}

@Composable
fun Header() {
    Row(
        modifier =
            Modifier
                .fillMaxWidth()
                .padding(bottom = 32.px),
        horizontalArrangement = Arrangement.SpaceBetween,
        verticalAlignment = CenterVertically,
    ) {
        Column {
            SpanText(
                "Gallery",
                modifier =
                    Modifier
                        .fontSize(32.px)
                        .fontWeight(FontWeight.Bold)
                        .color(text),
            )
            SpanText(
                "Browse through your photo collection",
                modifier =
                    Modifier
                        .fontSize(16.px)
                        .color(textSecondary)
                        .padding(top = 8.px),
            )
        }
        ThemeToggleButton()
    }
}

@Composable
fun GalleryContent(data: GalleryIndex) {
    val expandedState = remember { mutableStateMapOf<String, Boolean>() }

    Surface(
        modifier =
            Modifier
                .fillMaxWidth()
                .backgroundColor(surface)
                .borderRadius(16.px)
                .padding(24.px)
                .defaultBoxShadow(),
    ) {
        Column {
            if (data.folders.isNotEmpty()) {
                SpanText(
                    "Folders",
                    modifier =
                        Modifier
                            .fontSize(20.px)
                            .fontWeight(Medium)
                            .color(text)
                            .padding(bottom = 16.px),
                )

                data.folders.sortedBy { it.name }.forEach { folder ->
                    FolderView(folder, URL, expandedState)
                }
            }

            if (data.images.isNotEmpty()) {
                SpanText(
                    "Recent Photos",
                    modifier =
                        Modifier
                            .fontSize(20.px)
                            .fontWeight(Medium)
                            .color(text)
                            .padding(top = 32.px, bottom = 16.px),
                )

                Row(
                    modifier =
                        Modifier
                            .fillMaxWidth()
                            .flexWrap(FlexWrap.Wrap),
                    horizontalArrangement = Arrangement.Center,
                ) {
                    data.images.forEach { image ->
                        val imagePath = "$URL/$image"
                        ImageCard(imagePath) {
                            window.open(imagePath, "_blank")
                        }
                    }
                }
            }
        }
    }
}

@Composable
fun LoadingIndicator() {
    Surface(
        modifier =
            Modifier
                .fillMaxWidth()
                .padding(32.px),
    ) {
        Column(
            modifier = Modifier.fillMaxWidth(),
            horizontalAlignment = CenterHorizontally,
        ) {
            SpanText(
                "Loading gallery...",
                modifier =
                    Modifier
                        .fontSize(16.px)
                        .color(textSecondary),
            )
        }
    }
}

fun fetchGalleryData(onDataLoaded: (GalleryIndex?) -> Unit) =
    XMLHttpRequest().apply {
        open("GET", "$URL/index.json")
        onload = {
            if (status == 200.toShort()) {
                onDataLoaded(Json.decodeFromString<GalleryIndex>(responseText))
            }
        }
        send()
    }
